import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const inputVariants = cva(
  'block w-full appearance-none border bg-white text-gray-900 placeholder-gray-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500',
  {
    variants: {
      variant: {
        default:
          'border-gray-300 hover:border-gray-400 focus-visible:border-primary-500 focus-visible:ring-primary-500/20 dark:border-gray-700 dark:hover:border-gray-600',
        error:
          'border-red-500 focus-visible:border-red-500 focus-visible:ring-red-500/20 dark:border-red-500',
        success:
          'border-green-500 focus-visible:border-green-500 focus-visible:ring-green-500/20 dark:border-green-500',
      },
      size: {
        sm: 'h-8 rounded-md px-2.5 text-sm',
        md: 'h-10 rounded-md px-3 text-base',
        lg: 'h-12 rounded-lg px-4 text-lg',
      },
    },
    defaultVariants: { variant: 'default', size: 'md' },
  }
)

type BaseInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>

export interface InputProps
  extends BaseInputProps, VariantProps<typeof inputVariants> {
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size, leftIcon, rightIcon, ...rest }, ref) => {
    if (!leftIcon && !rightIcon) {
      return (
        <input
          ref={ref}
          className={cn(inputVariants({ variant, size, className }))}
          {...rest}
        />
      )
    }

    return (
      <div className="relative">
        {leftIcon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          className={cn(
            inputVariants({ variant, size }),
            leftIcon && 'pl-9',
            rightIcon && 'pr-9',
            className
          )}
          {...rest}
        />
        {rightIcon && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            {rightIcon}
          </span>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export { inputVariants }
